import { useMemo } from "react";

/**
 * Custom hook for calculating bid sheet totals
 * Reusable for main estimates and change orders
 */
export const useBidSheetCalculations = (bidSheet) => {
  return useMemo(() => {
    if (!bidSheet) {
      return {
        materialSubtotal: 0,
        laborSubtotal: 0,
        equipmentSubtotal: 0,
        miscSubtotal: 0,
        taxableMaterials: 0,
        burdenAmount: 0,
        subtotalBeforeProfit: 0,
        profitAmount: 0,
        taxAmount: 0,
        grandTotal: 0,
        pricePerSqft: 0,
      };
    }

    // Materials card totals (from Materials section)
    const materialItems = bidSheet.material_items || [];
    const materialDirectSubtotal = materialItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const materialMortarSubtotal = materialItems.reduce((sum, item) => sum + (item.mortar_total || 0), 0);
    const materialsCardTotal = materialDirectSubtotal + materialMortarSubtotal;
    
    // Misc section breakdown
    const miscItems = bidSheet.misc_items || [];
    // Only count the item's direct total when its dollars aren't already routed
    // to material/labor/mortar via allocation. Falling back to unit_price * quantity
    // would double-count allocated items (which intentionally have total=0).
    const miscDirectSubtotal = miscItems.reduce((sum, item) => {
      const hasAllocation =
        (item.material_cost || 0) !== 0 ||
        (item.labor_cost || 0) !== 0 ||
        (item.mortar_cost || 0) !== 0;
      if (hasAllocation) {
        return sum + (item.total || 0);
      }
      // Legacy fallback: items pre-dating the `total` field may only carry `amount`
      // or `unit_price` + `quantity`. Safe because no allocation overlap can exist.
      const itemTotal = item.total || item.amount || ((item.unit_price || 0) * (item.quantity || 1));
      return sum + itemTotal;
    }, 0);
    const miscMaterialSubtotal = miscItems.reduce((sum, item) => sum + (item.material_cost || 0), 0);
    const miscMortarSubtotal = miscItems.reduce((sum, item) => sum + (item.mortar_cost || 0), 0);
    // Note: Misc labor_cost creates labor_entries, which are counted in laborSubtotal below
    
    // Materials Subtotal for Bid Summary = Materials card + Misc material/mortar costs
    // This consolidates all material-type costs into one line
    const materialSubtotal = materialsCardTotal + miscMaterialSubtotal + miscMortarSubtotal;
    
    // Misc Subtotal for Bid Summary = Only direct unit_price items (not material/mortar/labor which go elsewhere)
    const miscSubtotal = miscDirectSubtotal;
    
    // Labor card total (all labor entries including from materials, equipment, misc)
    // Misc labor_cost automatically creates labor_entries which are summed here
    const laborEntries = bidSheet.labor_entries || [];
    const laborSubtotal = laborEntries.reduce((sum, entry) => sum + (entry.amount || 0), 0);
    
    // Equipment card totals (equipment is tax exempt)
    const equipmentItems = bidSheet.equipment_items || [];
    const equipmentDirectSubtotal = equipmentItems.reduce((sum, item) => sum + (item.total || 0), 0);
    const equipmentMaterialSubtotal = equipmentItems.reduce((sum, item) => sum + (item.material_cost || 0), 0);
    const equipmentSubtotal = equipmentDirectSubtotal + equipmentMaterialSubtotal;

    // Taxable materials = All material costs (for sales tax calculation)
    const taxableMaterials = materialSubtotal;
    
    // Calculations - Profit is calculated BEFORE sales tax
    const burdenPercent = bidSheet.burden_percent || 0;
    const profitPercent = bidSheet.profit_percent || 0;
    const salesTaxPercent = bidSheet.sales_tax_percent || 0;
    const taxExempt = bidSheet.tax_exempt || false;
    const scaffoldSqft = bidSheet.scaffold_sqft || 0;

    const burdenAmount = laborSubtotal * (burdenPercent / 100);
    const subtotalBeforeProfit = materialSubtotal + laborSubtotal + equipmentSubtotal + miscSubtotal + burdenAmount;
    const profitAmount = subtotalBeforeProfit * (profitPercent / 100);
    const taxAmount = taxExempt ? 0 : taxableMaterials * (salesTaxPercent / 100);
    const grandTotal = subtotalBeforeProfit + profitAmount + taxAmount;
    
    const pricePerSqft = scaffoldSqft > 0 ? grandTotal / scaffoldSqft : 0;

    return {
      materialSubtotal,      // Materials card + Misc material/mortar
      laborSubtotal,         // All labor entries (including from Misc)
      equipmentSubtotal,     // Equipment card total
      miscSubtotal,          // Only Misc unit_price items
      taxableMaterials,      // For sales tax calculation
      burdenAmount,
      subtotalBeforeProfit,
      profitAmount,
      taxAmount,
      grandTotal,
      pricePerSqft,
    };
  }, [bidSheet]);
};

export default useBidSheetCalculations;
